import { useState, useEffect, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Drawer,
  Button,
} from "@mui/material";
import { DataGrid, GridRowModel } from "@mui/x-data-grid";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as signalR from "@microsoft/signalr";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { buildDriverTableColumns } from "../../../data/FormColumns/Fleet/driverTableColumns";
import SettlementPayeeInfoModal from "../SettlementPayeeInfoModal/SettlementPayeeeInfoModal";
import { assignedDispatchersTableColumns } from "../../../data/FormColumns/Fleet/assignedDispatchersTableColumns";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";

const fetchDrivers = async (
  token: string | null | undefined,
  areActive: boolean,
) => {
  const { data } = await axios.get(`${apiVariable}/getAllDrivers`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: { areActive },
  });
  return data;
};

const DriverTable = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [openSettlementPayeeModal, setOpenSettlementPayeeModal] =
    useState(false);
  const [payTo, setPayTo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [areActive, setAreActive] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<any>(null);
  const [openAssignDispatcher, setOpenAssignDispatcher] = useState(false);
  const [dispatchers, setDispatchers] = useState([]);
  const [isLoadingDispatchersList, setIsLoadingDispatchersList] =
    useState(false);

  const assignDispatcherFormFields = [
    {
      name: "dispatcherId",
      label: "Dispatcher",
      type: "combo",
      dataKey: "dispatchers",
      validation: {
        required: "Dispatcher is required",
      },
    },
  ];

  const assignDispatcherInitialValues = {
    dispatcherId: "",
  };

  const {
    data: drivers,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["drivers", areActive],
    queryFn: () => fetchDrivers(token, areActive),
  });

  useEffect(() => {
    const driverConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiVariable}/driverHub`, {
        accessTokenFactory: () => token || "",
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    driverConnection.on("DriverUpdated", () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
    });

    driverConnection.start();

    return () => {
      driverConnection.stop();
    };
  }, [token, queryClient]);

  useEffect(() => {
    if (token) {
      refetch();
    }
  }, [areActive, token, refetch]);

  const { data: assignedDispatchers, isLoading: isLoadingAssignedDispatchers } =
    useQuery({
      queryKey: ["assignedDispatchers", selectedDriver?.id],
      queryFn: () => {
        const response = axios.get(
          `${apiVariable}/getAssignedDispatchersHistory/${selectedDriver.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        return response;
      },

      enabled: !!selectedDriver,
    });

  useEffect(() => {
    if (openAssignDispatcher) {
      setIsLoadingDispatchersList(true);
      axios
        .get(`${apiVariable}/getAllDispatchers`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const formattedData = response.data.map((dispatcher: any) => ({
            label: `${dispatcher.firstName} ${dispatcher.lastName} | ${dispatcher.email}`,
            id: dispatcher.id,
          }));
          setDispatchers(formattedData);
          setIsLoadingDispatchersList(false);
        })
        .catch((error) => {
          console.error("Error fetching dispatchers:", error);
          setIsLoadingDispatchersList(false);
        });
    }
  }, [openAssignDispatcher, token]);

  const handleAssignDispatcher = (values: any) => {
    axios
      .post(
        `${apiVariable}/assignDispatcherToDriver`,
        {
          driverId: selectedDriver.id,
          dispatcherId: getId(values.dispatcherId),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["assignedDispatchers", selectedDriver.id],
        });
        queryClient.invalidateQueries({ queryKey: ["drivers", true] });
        setOpenAssignDispatcher(false);
      })
      .catch((error) => {
        console.error("Error assigning dispatcher:", error);
      });
  };

  const updateMutation = useMutation({
    mutationFn: (row: any) =>
      axios.put(`${apiVariable}/updateDriver`, row, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
    },
  });

  const processRowUpdate = async (
    newRow: GridRowModel,
    oldRow: GridRowModel,
  ) => {
    try {
      const updated = { ...oldRow, ...newRow };
      await updateMutation.mutateAsync(updated);
      return updated;
    } catch (e) {
      return oldRow;
    }
  };

  const filteredDrivers = useMemo(() => {
    if (!drivers) return [];

    const normalize = (value: string) => value.toLowerCase();
    const sanitize = (value: string) =>
      normalize(value).replace(/[^a-z0-9]/g, "");

    const normalizedSearchTerm = normalize(searchTerm);
    const sanitizedSearchTerm = sanitize(searchTerm);

    return drivers.filter((driver: any) =>
      [
        driver.firstName,
        driver.lastName,
        driver.phoneNumber,
        driver.assignedTruckUnitNumber,
        driver.assignedTrailerUnitNumber,
        driver.assignedDispatcher,
      ].some((field) => {
        const valueString = field?.toString() || "";
        const normalizedValue = normalize(valueString);
        const sanitizedValue = sanitize(valueString);

        return (
          normalizedValue.includes(normalizedSearchTerm) ||
          (sanitizedSearchTerm.length > 0 &&
            sanitizedValue.includes(sanitizedSearchTerm))
        );
      }),
    );
  }, [drivers, searchTerm]);

  const handleSwitchChange = (event: any) => {
    setAreActive(event.target.checked);
  };

  const columns = useMemo(
    () => buildDriverTableColumns(!areActive),
    [areActive],
  );

  return (
    <Box className="drivers-table-container" height={900}>
      <Box
        className="table-controls"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 3,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={areActive}
              onChange={handleSwitchChange}
              color="primary"
            />
          }
          label={`${areActive ? "Active" : "Inactive"} Drivers`}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            position: "relative",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by Name, Phone Number, Truck, Trailer or Dispatcher"
            sx={{ maxWidth: "100%", width: 400 }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box className="table-error">
          <Typography color="error">Error: {error.message}</Typography>
        </Box>
      ) : (
        <PersistedDataGrid
          tableName="drivers"
          rows={filteredDrivers}
          columns={columns}
          compactViewConfig={{
            primaryField: "firstName",
            secondaryFields: ["driverCode", "phoneNumber"],
            detailFields: [
              "driverType",
              "company",
              "payTo",
              "payType",
              "usedEld",
              "assignedTruckUnitNumber",
              "assignedTrailerUnitNumber",
              "assignedDispatcher",
            ],
            actionFields: ["actions", "delete"],
            getCardTitle: (row) =>
              [row.firstName, row.lastName].filter(Boolean).join(" ") ||
              "Unknown Driver",
          }}
          onRowClick={(params) => {
            setSelectedDriver(params.row);
            setDrawerOpen(true);
          }}
          onCellClick={(params, event) => {
            if (params.field === "payTo") {
              event.stopPropagation();
              setOpenSettlementPayeeModal(true);
              setPayTo(params.row.payTo);
            } else if (params.field === "usedEld") {
              event.stopPropagation();
            }
          }}
          processRowUpdate={processRowUpdate}
          sx={{ height: 700 }}
        />
      )}

      <SettlementPayeeInfoModal
        payTo={payTo}
        open={openSettlementPayeeModal}
        onClose={() => setOpenSettlementPayeeModal(false)}
      />

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ "& .MuiDrawer-paper": { top: 64, height: "calc(100% - 64px)" } }}
      >
        <Box sx={{ maxWidth: "100%", width: 800, p: 2 }}>
          <Button
            onClick={() => setDrawerOpen(false)}
            sx={{ display: { xs: "inline-flex", lg: "none" }, mb: 1 }}
          >
            Close dispatcher details
          </Button>
          <Typography variant="h6">
            Assign Dispatcher to{" "}
            {selectedDriver ? selectedDriver.firstName : ""}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              setOpenAssignDispatcher(true);
            }}
            sx={{ mt: 2 }}
          >
            Assign Dispatcher
          </Button>

          {isLoadingAssignedDispatchers ? (
            <CircularProgress sx={{ mt: 2 }} />
          ) : (
            <DataGrid
              rows={assignedDispatchers?.data || []}
              columns={assignedDispatchersTableColumns}
              autoHeight
              sx={{ mt: 2 }}
            />
          )}
        </Box>
      </Drawer>

      {openAssignDispatcher && (
        <GenericFormPopup
          formFields={assignDispatcherFormFields}
          initialValues={assignDispatcherInitialValues}
          title="Assign Dispatcher"
          onSubmit={(values) => {
            handleAssignDispatcher(values);
          }}
          open={openAssignDispatcher}
          onClose={() => setOpenAssignDispatcher(false)}
          loading={isLoadingDispatchersList}
          autocompleteData={{ dispatchers: dispatchers }}
        />
      )}
    </Box>
  );
};

export default DriverTable;
